import { z } from 'zod';
import { PREMIUM_PLAN } from '../premium';
import {
  GAME_CONFIG,
  CARD_AURAS,
  CARD_COLOR_KEYS,
  CARD_EMBLEMS,
  CARD_FINISHES,
  CARD_FRAMES,
  CARD_NICKNAME_MAX,
  CARD_PORTRAITS,
  CARD_SIGNATURE_MAX,
  MESSAGE_REACTIONS,
  REFERRAL_CODE_PATTERN,
  REPORT_REASONS,
  VISIBILITY_LEVELS,
  normalizeReferralCode,
} from '../economy';

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, 'Adresse email trop longue')
  .pipe(z.email({ error: 'Adresse email invalide' }));

export const usernameSchema = z
  .string()
  .trim()
  .min(USERNAME_MIN, `${USERNAME_MIN} caractères minimum`)
  .max(USERNAME_MAX, `${USERNAME_MAX} caractères maximum`)
  .regex(/^[A-Za-z0-9_.-]+$/, 'Lettres, chiffres, point, tiret et underscore uniquement');

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `${PASSWORD_MIN} caractères minimum`)
  .max(PASSWORD_MAX, `${PASSWORD_MAX} caractères maximum`)
  .refine((value) => /\p{L}/u.test(value) && /[^\p{L}]/u.test(value), {
    error: 'Utilise au moins une lettre et un chiffre ou symbole',
  });

const turnstileTokenSchema = z.string().max(2048).optional();

export const referralCodeSchema = z
  .string()
  .max(32, 'Code de parrainage invalide')
  .transform(normalizeReferralCode)
  .pipe(z.string().regex(REFERRAL_CODE_PATTERN, 'Code de parrainage invalide'));

export const registerSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  password: passwordSchema,
  acceptTerms: z.literal(true, { error: "Tu dois accepter les conditions d'utilisation" }),
  turnstileToken: turnstileTokenSchema,
  
  referralCode: z
    .string()
    .max(32, 'Code de parrainage invalide')
    .optional()
    .transform((value) => (value && value.trim() ? value : undefined))
    .pipe(referralCodeSchema.optional()),
});

export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  identifier: z.string().trim().min(1, 'Indique ton email ou ton pseudo').max(254),
  password: z.string().min(1, 'Indique ton mot de passe').max(PASSWORD_MAX),
  turnstileToken: turnstileTokenSchema,
});

export type LoginInput = z.infer<typeof loginSchema>;

export const deleteAccountSchema = z.object({
  password: z.string().min(1, 'Indique ton mot de passe').max(PASSWORD_MAX),
});

export type DeleteAccountInput = z.infer<typeof deleteAccountSchema>;

const cardTextSchema = (max: number) => z.string().trim().max(max)
  .refine((value) => !/[\u0000-\u001F\u007F\u202A-\u202E\u2066-\u2069]/u.test(value), {
    error: 'Ce texte contient des caractères non autorisés.',
  })
  .transform((value) => value || null)
  .nullable()
  .optional();

export const cardCustomizationSchema = z.strictObject({
  nickname: cardTextSchema(CARD_NICKNAME_MAX),
  nameColor: z.enum(CARD_COLOR_KEYS).nullable().optional(),
  frame: z.enum(CARD_FRAMES).nullable().optional(),
  emblem: z.enum(CARD_EMBLEMS).nullable().optional(),
  haloColor: z.enum(CARD_COLOR_KEYS).nullable().optional(),
  finish: z.enum(CARD_FINISHES).nullable().optional(),
  signature: cardTextSchema(CARD_SIGNATURE_MAX),
  aura: z.enum(CARD_AURAS).nullable().optional(),
  portrait: z.enum(CARD_PORTRAITS).nullable().optional(),
});

export const cardCustomizationPatchSchema = cardCustomizationSchema.refine(
  (value) => Object.keys(value).length > 0,
  { error: 'Choisis une personnalisation à modifier.' },
);

export type CardCustomizationInput = z.infer<typeof cardCustomizationPatchSchema>;

export const reportMessageSnapshotSchema = z.object({
  messageId: z.string(),
  content: z.string().nullable(),
  createdAt: z.string(),
  deleted: z.boolean(),
  sharedCard: z.object({
    characterId: z.number().int().positive(),
    name: z.string(),
    upgradeLevel: z.number().int().positive().nullable(),
    customization: cardCustomizationSchema,
  }).nullable().optional(),
});


export const idempotencyKeySchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'Clé d’idempotence invalide');

export const visibilitySchema = z.enum(VISIBILITY_LEVELS);
export const messageReactionSchema = z.enum(MESSAGE_REACTIONS);
export const reportReasonSchema = z.enum(REPORT_REASONS);

export const messageContentSchema = z
  .string()
  .trim()
  .min(1, 'Écris un message.')
  .max(GAME_CONFIG.social.maxMessageLength, `Message trop long (${GAME_CONFIG.social.maxMessageLength} caractères max).`)
  .refine((value) => !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(value), {
    error: 'Le message contient des caractères non autorisés.',
  });

export const sendMessageSchema = z
  .object({
    conversationId: z.string().uuid(),
    content: messageContentSchema.optional(),
    sharedCharacterId: z.number().int().positive().optional(),
    clientId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'Identifiant client invalide'),
  })
  .refine((value) => Boolean(value.content?.length) || value.sharedCharacterId !== undefined, {
    error: 'Ajoute un message ou partage une carte.',
  });

export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const profileSettingsSchema = z.object({
  profileVisibility: visibilitySchema.optional(),
  collectionVisibility: visibilitySchema.optional(),
  tradeVisibility: visibilitySchema.optional(),
  showAnima: z.boolean().optional(),
  showPresence: z.boolean().optional(),
  allowFriendRequests: z.boolean().optional(),
  notificationPrefs: z
    .object({
      friend_request: z.boolean().optional(),
      friend_accepted: z.boolean().optional(),
      message: z.boolean().optional(),
      reward: z.boolean().optional(),
      market: z.boolean().optional(),
      trade: z.boolean().optional(),
      free_booster: z.boolean().optional(),
      login_calendar: z.boolean().optional(),
    })
    .optional(),
});

export type ProfileSettingsInput = z.infer<typeof profileSettingsSchema>;

export const showcaseSchema = z.object({
  characterIds: z.array(z.number().int().positive()).max(PREMIUM_PLAN.showcaseSlots),
});

export const reportSchema = z.object({
  targetUserId: z.string().uuid(),
  messageId: z.string().uuid().optional(),
  context: z
    .string()
    .regex(/^(sale|trade):[0-9a-f-]{36}$/, 'Contexte de signalement invalide')
    .optional(),
  reason: reportReasonSchema,
  details: z.string().trim().max(500).optional(),
  alsoBlock: z.boolean().optional(),
});

export type ReportInput = z.infer<typeof reportSchema>;

export const friendRequestSchema = z.object({
  username: usernameSchema,
});

export const createListingSchema = z.object({
  characterId: z.number().int().positive(),
  price: z.number().int().positive(),
  description: z.string().trim().max(200).optional(),
});

export type CreateListingInput = z.infer<typeof createListingSchema>;

export const createAuctionSchema = z
  .object({
    characterId: z.number().int().positive(),
    startingPrice: z.number().int().positive(),
    buyoutPrice: z.number().int().positive().optional(),
    durationHours: z.coerce.number().pipe(z.union([z.literal(1), z.literal(6), z.literal(12), z.literal(24)])),
    description: z.string().trim().max(200).optional(),
  })
  .refine(
    (value) =>
      value.buyoutPrice === undefined ||
      value.buyoutPrice > value.startingPrice + GAME_CONFIG.market.auctionIncrementBase,
    {
      error: 'Le prix d’achat immédiat doit dépasser la mise de départ.',
      path: ['buyoutPrice'],
    },
  );

export type CreateAuctionInput = z.infer<typeof createAuctionSchema>;

export const placeBidSchema = z.object({
  amount: z.number().int().positive(),
});

export type PlaceBidInput = z.infer<typeof placeBidSchema>;

const tradeCharacterIdsSchema = z
  .array(z.number().int().positive())
  .max(PREMIUM_PLAN.maxTradeCardsPerSide);

export const createTradeSchema = z
  .object({
    username: usernameSchema,
    offerCharacterIds: tradeCharacterIdsSchema.default([]),
    requestCharacterIds: tradeCharacterIdsSchema.default([]),
    offerAnima: z.number().int().min(0).max(PREMIUM_PLAN.maxTradeAnima).default(0),
    requestAnima: z.number().int().min(0).max(PREMIUM_PLAN.maxTradeAnima).default(0),
    note: z.string().trim().max(200).optional(),
    durationMs: z
      .number()
      .int()
      .min(GAME_CONFIG.market.tradeMinDurationMs)
      .max(GAME_CONFIG.market.tradeMaxDurationMs)
      .default(GAME_CONFIG.market.tradeMinDurationMs),
  })
  .refine((value) => value.offerAnima === 0 || value.requestAnima === 0, {
    error: 'L’Anima ne peut venir que d’un seul côté.',
  })
  .refine((value) => value.offerCharacterIds.length + value.requestCharacterIds.length > 0, {
    error: 'Ajoute au moins une carte à l’échange.',
  });

export type CreateTradeInput = z.infer<typeof createTradeSchema>;

export const counterTradeSchema = z
  .object({
    offerCharacterIds: tradeCharacterIdsSchema.default([]),
    requestCharacterIds: tradeCharacterIdsSchema.default([]),
    offerAnima: z.number().int().min(0).max(PREMIUM_PLAN.maxTradeAnima).default(0),
    requestAnima: z.number().int().min(0).max(PREMIUM_PLAN.maxTradeAnima).default(0),
    note: z.string().trim().max(200).optional(),
    durationMs: z
      .number()
      .int()
      .min(GAME_CONFIG.market.tradeMinDurationMs)
      .max(GAME_CONFIG.market.tradeMaxDurationMs)
      .default(GAME_CONFIG.market.tradeMinDurationMs),
  })
  .refine((value) => value.offerAnima === 0 || value.requestAnima === 0, {
    error: 'L’Anima ne peut venir que d’un seul côté.',
  })
  .refine((value) => value.offerCharacterIds.length + value.requestCharacterIds.length > 0, {
    error: 'Ajoute au moins une carte à l’échange.',
  });

export type CounterTradeInput = z.infer<typeof counterTradeSchema>;

export const confirmTradeSchema = z.object({
  revision: z.number().int().positive(),
});

export type ConfirmTradeInput = z.infer<typeof confirmTradeSchema>;
